import React, { useContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import useFetchData from '../../../../../Hooks/useFetchData';
import {
	getMinMaxAvgByRoleFilters,
	getMinMaxAvgRoleDate,
} from '../../../../../utils/apiRequests';
import {
	ActionsWrapper,
	FilterWrapper,
	Header,
	LoadingContainer,
	NoData,
	SectionWrapper,
	Title,
	TooltipContainer,
	TooltipItem,
	TooltipItemName,
	TooltipItemTitle,
	TooltipItemValue,
} from './Charts.styles';
import { useState } from 'react';
import MultiSelect from '../../Components/Multiselect/MultiSelect';
import { transformKeyToLabel } from '../../../../../utils/filterNames';
import { PAGE_SIZE } from 'SpeedToMarket/utils';
import {
	ApButtonIcon,
	ApButtonMain,
	ApButtonSecondary,
	ApLoaderDefault,
	ApLoaderText,
	ApMenu,
	ApMenuItem,
	colors,
} from '@alixpartners/ui-components';
import Pagination from 'components/Pagination/Pagination';
import { EXPORT_OPTIONS } from '../utils';
import { renderToString } from 'react-dom/server';
import { SettingsContext } from 'contexts/SettingsContext';
import { formatNumberWithDecimals } from 'utils/number';
import { isNull, isUndefined } from 'lodash';
import { FONT_STYLE } from '../../../../utils';

const INITIAL_FILTER = {
	role: [
		'Associate',
		'Counsel',
		'Jr. Associate',
		'Jr. Partner',
		'Legal Assistant',
		'Paralegal',
		'Partner',
		'Sr. Associate',
		'Sr. Partners',
	],
};

function createChain(chain, key, values) {
	let current = chain;

	// Traverse to the deepest child node
	while (current.child) {
		current = current.child;
	}
	current.child = { [key]: values };
	return { ...chain };
}

function removeFromChain(obj, keyToRemove) {
	if (!obj || typeof obj !== 'object') return obj;

	if (obj[keyToRemove] && obj.child) {
		delete obj.child;
	} else if (obj.child) {
		obj.child = removeFromChain(obj.child, keyToRemove);
	}
	return { ...obj };
}

function getValueByKey(obj, keyToFind) {
	if (!obj || typeof obj !== 'object') return undefined;

	if (keyToFind in obj) {
		return obj[keyToFind];
	}

	if (obj.child) {
		return getValueByKey(obj.child, keyToFind);
	}
	return undefined;
}

function getDependentKeys(obj, targetKey) {
	if (!obj || typeof obj !== 'object') return null;

	if (obj.hasOwnProperty(targetKey)) {
		const result = [];
		let current = obj;

		while (current.child) {
			current = current.child;
			result.push(...Object.keys(current));
		}
		return result;
	}

	for (let key in obj) {
		if (typeof obj[key] === 'object') {
			const result = getDependentKeys(obj[key], targetKey);
			if (result) return result;
		}
	}

	return null;
}

const MinMaxAverageByRole = ({ chartTitle }) => {
	const chartRef = useRef();
	const { hideDecimals } = useContext(SettingsContext);
	const [selectedFilters, setSelectedFilters] = useState(INITIAL_FILTER);
	const [chartParams, setChartParams] = useState(INITIAL_FILTER);
	const [currentPage, setCurrentPage] = useState(1);
	const [filtersChain, setFiltersChain] = useState({});
	const { data: filterData, isLoading: isFilterLoading } = useFetchData(
		getMinMaxAvgByRoleFilters,
		selectedFilters
	);
	const { data: chartData, isLoading: isChartLoading } = useFetchData(
		getMinMaxAvgRoleDate,
		chartParams
	);

	const onFilterChangeHandler = (key, value, options) => {
		if (!Object.keys(filtersChain).length) {
			const newChain = createChain(
				filtersChain,
				key,
				options.map(x => x.value)
			);
			setFiltersChain(newChain);
			setSelectedFilters(prevFilters => ({ ...prevFilters, [key]: value }));
		} else {
			const oldValue = getValueByKey(filtersChain, key);
			if (isUndefined(oldValue)) {
				const newChain = createChain(
					filtersChain,
					key,
					options.map(x => x.value)
				);
				setFiltersChain(newChain);
				setSelectedFilters(prevFilters => ({ ...prevFilters, [key]: value }));
			} else {
				const dependentFilters = getDependentKeys(filtersChain, key);

				if (dependentFilters.length === 0) {
					setSelectedFilters(prevFilters => ({ ...prevFilters, [key]: value }));
				} else {
					if (!isNull(dependentFilters)) {
						if (dependentFilters.length > 0) {
							const filteredData = dependentFilters.filter(x => x !== 'child');
							setSelectedFilters(prevData => {
								const updatedData = { ...prevData };
								filteredData.forEach(key => delete updatedData[key]);
								return updatedData;
							});
						}
					}
				}
				const newChain = removeFromChain(filtersChain, key);
				setFiltersChain(newChain);
			}
		}
	};

	const onApplyFilterHandler = () => {
		setChartParams({
			...selectedFilters,
		});
	};

	const paginatedData = useMemo(
		() => {
			if (!chartData?.data || chartData.data.length === 0) return null;

			const startIndex = (currentPage - 1) * PAGE_SIZE;
			const endIndex = startIndex + PAGE_SIZE;
			const dataForPage = chartData.data.slice(startIndex, endIndex);
			const sortByRole = dataForPage.sort((a, b) =>
				a?.role.localeCompare(b?.role)
			);

			return sortByRole.map(item => ({
				role: item.role,
				min: item.min,
				max: item.max,
				median: item.median,
				q1: item.q1,
				q3: item.q3,
			}));
		},
		[chartData, currentPage]
	);

	const chartOptions = useMemo(
		() => {
			if (!paginatedData) return null;

			const categories = paginatedData.map(item => item.role);
			const data = paginatedData.map(item => [
				item.min,
				item.q1,
				item.median,
				item.q3,
				item.max,
			]);

			return {
				chart: { type: 'boxplot' },
				exporting: { enabled: false },
				credits: { enabled: false },
				title: { text: '' },
				xAxis: {
					categories,
					title: {
						text: 'Role',
						style: { ...FONT_STYLE },
					},
					gridLineWidth: 0,
					lineColor: colors.black,
					lineWidth: 1,
				},
				yAxis: {
					title: {
						text: 'Rate per Hour (USD)',
						style: { ...FONT_STYLE },
					},
					gridLineWidth: 0,
					lineColor: colors.black,
					lineWidth: 1,
				},
				tooltip: {
					shared: true,
					useHTML: true,
					backgroundColor: colors.white,
					borderWidth: 0,
					borderRadius: 2,
					outside: true,
					formatter: function() {
						const { x, point } = this.points[0];
						const { options } = point;
						const { high, low, median, q1, q3 } = options;
						return renderToString(
							<TooltipContainer>
								<TooltipItemTitle>{x}</TooltipItemTitle>
								<TooltipItem key={'High'}>
									<span>
										<TooltipItemName>High</TooltipItemName>
									</span>
									<TooltipItemValue>
										{formatNumberWithDecimals(high, hideDecimals)}
									</TooltipItemValue>
								</TooltipItem>

								<TooltipItem key={'Q1'}>
									<span>
										<TooltipItemName>Q1</TooltipItemName>
									</span>
									<TooltipItemValue>
										{formatNumberWithDecimals(q1, hideDecimals)}
									</TooltipItemValue>
								</TooltipItem>

								<TooltipItem key={'Median'}>
									<span>
										<TooltipItemName>Median</TooltipItemName>
									</span>
									<TooltipItemValue>
										{formatNumberWithDecimals(median, hideDecimals)}
									</TooltipItemValue>
								</TooltipItem>

								<TooltipItem key={'Q3'}>
									<span>
										<TooltipItemName>Q3</TooltipItemName>
									</span>
									<TooltipItemValue>
										{formatNumberWithDecimals(q3, hideDecimals)}
									</TooltipItemValue>
								</TooltipItem>

								<TooltipItem key={'Low'}>
									<span>
										<TooltipItemName>Low</TooltipItemName>
									</span>
									<TooltipItemValue>
										{formatNumberWithDecimals(low, hideDecimals)}
									</TooltipItemValue>
								</TooltipItem>
							</TooltipContainer>
						);
					},
				},
				series: [
					{
						name: '',
						showInLegend: false,
						data: data,
						color: '#032E45',
					},
				],
			};
		},
		[paginatedData, hideDecimals]
	);

	const handleChartExport = option => {
		const chartInstance = chartRef?.current?.chart;
		if (chartInstance && option.value === 'png') {
			chartInstance.exportChart(
				{
					filename: chartTitle,
				},
				{
					chart: { width: 1200 },
				}
			);
		}
		if (option.value === 'xlsx') {
			this.handleChartDataExport();
		}
	};

	const onClearFilterHandler = () => {
		setSelectedFilters(INITIAL_FILTER);
		setFiltersChain([]);
	};

	const isAnyFilterApplied = useMemo(
		() => Object.keys(selectedFilters).length > 1,
		[selectedFilters]
	);

	return (
		<SectionWrapper>
			<Header>
				<Title>{chartTitle}</Title>
				<ApMenu button={<ApButtonIcon iconName="download" />}>
					{EXPORT_OPTIONS.map(option => (
						<ApMenuItem
							key={option.value}
							onClick={() => handleChartExport(option)}
						>
							{option.label}
						</ApMenuItem>
					))}
				</ApMenu>
			</Header>

			<>
				{!isNull(filterData) && (
					<FilterWrapper>
						{Object.keys(filterData?.data || {}).map((key, index) => {
							const oldOptions = getValueByKey(filtersChain, key);
							const options = !oldOptions
								? (filterData.data[key] || []).map(option => ({
										label: option,
										value: option,
								  }))
								: oldOptions.map(option => ({
										label: option,
										value: option,
								  }));

							const selectedValue =
								options.length === 1
									? [options[0].value]
									: selectedFilters[key] || [];
							return (
								<MultiSelect
									key={`${filterData.data[key]}_${index}`}
									name={filterData.data[key]}
									label={`Select ${transformKeyToLabel(key)}`}
									options={options}
									selected={selectedValue}
									onChange={value => onFilterChangeHandler(key, value, options)}
									isLoading={isFilterLoading}
								/>
							);
						})}
						<ActionsWrapper>
							<div>
								<ApButtonMain
									className="actions"
									title="Apply Filter"
									type="button"
									onClick={onApplyFilterHandler}
									disabled={isChartLoading || isFilterLoading}
								>
									Apply Filter
								</ApButtonMain>
							</div>
							<div>
								{isAnyFilterApplied && (
									<ApButtonSecondary
										className="actions"
										title="Clear Filter"
										type="button"
										onClick={onClearFilterHandler}
										disabled={isChartLoading}
									>
										Clear Filter
									</ApButtonSecondary>
								)}
							</div>
						</ActionsWrapper>
					</FilterWrapper>
				)}
			</>
			{/* )} */}

			{isChartLoading ? (
				<LoadingContainer>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</LoadingContainer>
			) : (
				<>
					{!paginatedData ? (
						<NoData>No data available</NoData>
					) : (
						<>
							<HighchartsReact
								highcharts={Highcharts}
								options={chartOptions}
								ref={chartRef}
							/>
							<Pagination
								currentPage={currentPage}
								totalCount={chartData?.data?.length || 0}
								pageSize={PAGE_SIZE}
								onPageChange={page => setCurrentPage(page)}
								position="center"
							/>
						</>
					)}
				</>
			)}
		</SectionWrapper>
	);
};

MinMaxAverageByRole.propTypes = {
	chartTitle: PropTypes.string.isRequired,
};

export default MinMaxAverageByRole;
